/** Default marketing copy per built-in flyer layout. */

export const DEFAULT_FLYER_COPY = {
  'pleating-suede': {
    brandName: 'YUVI',
    brandSuffix: 'FABRICS',
    collectionLabel: 'Imported Fabric',
    productTitle: 'PLEATING SUEDE',
    audienceLine: "For Women's Wear",
    features: ['Soft Touch', 'Premium Quality', 'Pleated Texture', 'Stylish & Elegant'],
    tagline: 'Premium Fabric. Perfect Pleats. Timeless Elegance.',
    sealText: 'Imported Pleating Suede Fabric',
    infoLines: [
      'For Boutique Owners, Designers & Manufacturers',
      'Order Based Work Samples Available',
      'Sample Folder Available',
    ],
    heroQuote: 'Soft Feel, Beautiful Fall, Luxury Look.',
    perfectForTitle: 'Perfect For',
    perfectFor: ['Western Wear', 'Co-ord Sets', 'Dresses', 'Tops & Tunics', 'Skirts & More'],
    footerText: 'YUVI FABRICS — Elegance in Every Thread',
  },
  'georgette-glow': {
    brandName: 'AURA',
    brandSuffix: 'TEXTILES',
    collectionLabel: 'Lightweight Collection',
    productTitle: 'SOFT GEORGETTE',
    audienceLine: 'For Daywear & Occasion',
    features: ['Airy Drape', 'Sheer Finish', 'Breathable', 'Summer Ready'],
    tagline: 'Floats like air. Falls like silk. Feels like summer.',
    sealText: 'Imported Soft Georgette',
    infoLines: [
      'Ideal for Boutiques & Design Studios',
      'Cut Lengths & Wholesale Available',
      'Shade Cards on Request',
    ],
    heroQuote: 'Light as air, rich in colour.',
    perfectForTitle: 'Perfect For',
    perfectFor: ['Anarkalis', 'Dupattas', 'Saree Blouses', 'Kurtis', 'Layered Looks'],
    footerText: 'AURA TEXTILES — Weave Your Story',
  },
  'linen-essentials': {
    brandName: 'EARTH',
    brandSuffix: 'LOOM',
    collectionLabel: 'Pure & Honest',
    productTitle: 'LINEN BLEND',
    audienceLine: 'For Contemporary Wear',
    features: ['Natural Fibre', 'Breathable', 'Year-Round', 'Easy Tailoring'],
    tagline: 'Crisp hand. Soft crease. Lived-in luxury.',
    sealText: 'Premium Linen Blend Fabric',
    infoLines: [
      'For Labels, Tailors & Home Ateliers',
      'Minimum Order Friendly',
      'Swatch Books Ready',
    ],
    heroQuote: 'Naturally refined. Effortlessly worn.',
    perfectForTitle: 'Perfect For',
    perfectFor: ['Shirts', 'Co-ords', 'Trousers', 'Resort Wear', 'Workwear'],
    footerText: 'EARTH LOOM — Cloth With Character',
  },
  'velvet-royale': {
    brandName: 'NOIR',
    brandSuffix: 'ATELIER',
    collectionLabel: 'Evening Collection',
    productTitle: 'SILK VELVET',
    audienceLine: 'For Bridal & Festive',
    features: ['Regal Hand', 'Deep Pile', 'Rich Colour', 'Occasion Ready'],
    tagline: 'Depth you can feel. Sheen you can see.',
    sealText: 'Imported Silk Velvet',
    infoLines: [
      'For Bridal Houses & Couture Labels',
      'Exclusive Colourways',
      'Private Sample Appointments',
    ],
    heroQuote: 'Luxe pile. Timeless drama.',
    perfectForTitle: 'Perfect For',
    perfectFor: ['Lehengas', 'Sherwanis', 'Gowns', 'Blazers', 'Occasion Sets'],
    footerText: 'NOIR ATELIER — Opulence Woven In',
  },
  'cotton-story': {
    brandName: 'MILL',
    brandSuffix: '& CO',
    collectionLabel: 'Everyday Essential',
    productTitle: 'PRINT COTTON',
    audienceLine: 'For Kids & Family Wear',
    features: ['100% Cotton', 'Easy Care', 'Soft Hand', 'Everyday Wear'],
    tagline: 'Soft on skin. Strong in colour. Made to last.',
    sealText: 'Premium Print Cotton',
    infoLines: [
      'For Garment Makers & Retail Brands',
      'Bulk & Sample Orders',
      'Print Catalogue Available',
    ],
    heroQuote: 'Comfort first. Style always.',
    perfectForTitle: 'Perfect For',
    perfectFor: ['Kurtis', 'Kidswear', 'Shirts', 'Home Textiles', 'Uniforms'],
    footerText: 'MILL & CO — Cotton That Cares',
  },
};

const STRING_KEYS = [
  'brandName',
  'brandSuffix',
  'collectionLabel',
  'productTitle',
  'audienceLine',
  'tagline',
  'sealText',
  'heroQuote',
  'perfectForTitle',
  'footerText',
];

const ARRAY_KEYS = ['features', 'infoLines', 'perfectFor'];

function clipStr(value, max = 200) {
  return String(value ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, max);
}

function clipList(value, fallback, maxItems, maxLen) {
  const source = Array.isArray(value) ? value : fallback;
  const out = source.map((item) => clipStr(item, maxLen)).filter(Boolean);
  if (out.length === 0) return [...fallback];
  return out.slice(0, maxItems);
}

/** Merge stored/partial copy onto layout defaults. */
export function resolveFlyerCopy(builtInKey, storedCopy) {
  const defaults = DEFAULT_FLYER_COPY[builtInKey];
  if (!defaults) return null;
  const incoming = storedCopy && typeof storedCopy === 'object' ? storedCopy : {};
  const merged = { ...defaults };

  for (const key of STRING_KEYS) {
    if (incoming[key] != null && String(incoming[key]).trim() !== '') {
      merged[key] = clipStr(incoming[key], key === 'footerText' || key === 'tagline' ? 240 : 120);
    }
  }

  merged.features = clipList(incoming.features, defaults.features, 4, 40);
  while (merged.features.length < defaults.features.length) {
    merged.features.push(defaults.features[merged.features.length]);
  }
  merged.features = merged.features.slice(0, 4);

  merged.infoLines = clipList(incoming.infoLines, defaults.infoLines, 3, 120);
  while (merged.infoLines.length < Math.min(3, defaults.infoLines.length)) {
    merged.infoLines.push(defaults.infoLines[merged.infoLines.length]);
  }
  merged.infoLines = merged.infoLines.slice(0, 3);

  merged.perfectFor = clipList(incoming.perfectFor, defaults.perfectFor, 8, 40);

  return merged;
}

/** Sanitize copy payload before sending to the API. */
export function sanitizeFlyerCopy(builtInKey, copy) {
  return resolveFlyerCopy(builtInKey, copy);
}

export function patchFlyerCopy(copy, path, value) {
  if (!copy) return copy;
  if (path.startsWith('features.')) {
    const idx = Number(path.split('.')[1]);
    const features = [...(copy.features || [])];
    features[idx] = value;
    return { ...copy, features };
  }
  if (path.startsWith('infoLines.')) {
    const idx = Number(path.split('.')[1]);
    const infoLines = [...(copy.infoLines || [])];
    infoLines[idx] = value;
    return { ...copy, infoLines };
  }
  if (path.startsWith('perfectFor.')) {
    const idx = Number(path.split('.')[1]);
    const perfectFor = [...(copy.perfectFor || [])];
    perfectFor[idx] = value;
    return { ...copy, perfectFor };
  }
  return { ...copy, [path]: value };
}

export { ARRAY_KEYS, STRING_KEYS };
